"use client";

import { useEffect, useRef, useState } from "react";

/**
 * AddToCalendarMenu
 * ─────────────────
 * Dropdown con scelta tra Google Calendar, Apple Calendar (download .ics)
 * e Outlook Live. Il file .ics viene gestito anche dai client desktop di
 * Apple Calendar / Outlook locale, quindi una stessa URL serve due target.
 *
 * I link a Google Calendar e Outlook Web sono "deep link" che aprono
 * direttamente la finestra di creazione evento già pre-compilata.
 *
 * Tutti i parametri arrivano già pronti dal server (la pagina è async),
 * così questo componente è pure-stateless: gestisce solo l'apertura del
 * menu.
 */
export interface AddToCalendarMenuProps {
  icsUrl: string;
  /** URL già costruito server-side per Google Calendar render. */
  googleCalendarUrl: string;
  /** URL già costruito server-side per Outlook Live deep-link. */
  outlookUrl: string;
}

export function AddToCalendarMenu({
  icsUrl,
  googleCalendarUrl,
  outlookUrl,
}: AddToCalendarMenuProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const escHandler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("keydown", escHandler);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("keydown", escHandler);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="inline-flex items-center gap-2 text-sm font-medium border border-[#1a3a5c]/20 text-[#1a3a5c] px-4 py-2 rounded-lg hover:bg-[#1a3a5c]/5 transition-colors"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
          aria-hidden
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
        Aggiungi al calendario
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className={`h-3 w-3 transition-transform ${open ? "rotate-180" : ""}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
          aria-hidden
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute left-0 z-30 mt-2 w-64 rounded-lg border border-neutral-200 bg-white shadow-lg overflow-hidden"
        >
          {/* Google Calendar */}
          <a
            role="menuitem"
            href={googleCalendarUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setOpen(false)}
            className="flex items-center gap-3 px-4 py-3 text-sm text-neutral-700 hover:bg-neutral-50 transition-colors"
          >
            <GoogleCalendarIcon />
            <span className="flex-1">Google Calendar</span>
            <span className="text-[10px] text-neutral-500">Web</span>
          </a>

          {/* Apple Calendar — usa lo .ics nativo. Su iOS/macOS apre
              direttamente Calendar.app. */}
          <a
            role="menuitem"
            href={icsUrl}
            download
            onClick={() => setOpen(false)}
            className="flex items-center gap-3 px-4 py-3 text-sm text-neutral-700 hover:bg-neutral-50 transition-colors border-t border-neutral-100"
          >
            <AppleCalendarIcon />
            <span className="flex-1">Apple Calendar</span>
            <span className="text-[10px] text-neutral-500">.ics</span>
          </a>

          {/* Outlook web */}
          <a
            role="menuitem"
            href={outlookUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setOpen(false)}
            className="flex items-center gap-3 px-4 py-3 text-sm text-neutral-700 hover:bg-neutral-50 transition-colors border-t border-neutral-100"
          >
            <OutlookIcon />
            <span className="flex-1">Outlook</span>
            <span className="text-[10px] text-neutral-500">Web</span>
          </a>

          {/* Download .ics generico — utile per Outlook desktop, Thunderbird,
              client che non rientrano negli altri target. */}
          <a
            role="menuitem"
            href={icsUrl}
            download
            onClick={() => setOpen(false)}
            className="flex items-center gap-3 px-4 py-3 text-sm text-neutral-700 hover:bg-neutral-50 transition-colors border-t border-neutral-100"
          >
            <DownloadIcon />
            <span className="flex-1">Altri (download .ics)</span>
          </a>
        </div>
      )}
    </div>
  );
}

/* ── Icone ────────────────────────────────────────────────────── */

function GoogleCalendarIcon() {
  return (
    <svg viewBox="0 0 200 200" className="w-5 h-5 shrink-0" aria-hidden>
      <path d="M152.6 47.4H47.4v105.2h105.2V47.4z" fill="#fff" />
      <path d="M152.6 200L200 152.6l-23.7-4-23.7 4-4.2 22 4.2 25.4z" fill="#ea4335" />
      <path d="M0 152.6v31.6c0 8.7 7.1 15.8 15.8 15.8h31.6l6.5-23.7-6.5-23.7-25.4-4z" fill="#188038" />
      <path d="M200 47.4V15.8C200 7.1 192.9 0 184.2 0h-31.6c-2.9 9.3-4.4 16.5-4.4 21.5 0 5.1 1.4 12.7 4.4 22.9l21 4.4 26.4-1.4z" fill="#1967d2" />
      <path d="M200 47.4h-47.4v105.2H200V47.4z" fill="#fbbc04" />
      <path d="M152.6 152.6H47.4V200h105.2v-47.4z" fill="#34a853" />
      <path d="M152.6 0H15.8C7.1 0 0 7.1 0 15.8v136.8h47.4V47.4h105.2V0z" fill="#4285f4" />
      <path d="M69 129.8c-3.9-2.7-6.7-6.5-8.2-11.6l9.1-3.8c.9 3.2 2.4 5.7 4.6 7.5 2.2 1.8 4.8 2.6 7.9 2.6 3.2 0 5.9-1 7.9-2.9s3.1-4.4 3.1-7.4c0-3.1-1.1-5.6-3.2-7.5-2.1-1.9-4.9-2.9-8.2-2.9h-5.3v-9h4.7c2.9 0 5.3-.8 7.3-2.3 2-1.5 3-3.7 3-6.5 0-2.5-.9-4.5-2.7-6-1.8-1.5-4.1-2.3-6.9-2.3-2.7 0-4.9.7-6.5 2.2-1.6 1.4-2.8 3.2-3.5 5.3l-9-3.8c1.2-3.4 3.4-6.5 6.7-9.1 3.3-2.6 7.5-3.9 12.6-3.9 3.8 0 7.2.7 10.2 2.2 3 1.5 5.4 3.5 7.1 6.1 1.7 2.6 2.5 5.5 2.5 8.8 0 3.3-.8 6.1-2.4 8.4-1.6 2.3-3.6 4.1-5.9 5.3v.5c3 1.3 5.5 3.2 7.5 5.7 1.9 2.5 2.9 5.6 2.9 9.2 0 3.6-.9 6.8-2.7 9.6-1.8 2.8-4.4 5-7.6 6.6-3.3 1.6-7 2.4-11 2.4-4.7 0-9.1-1.4-12.9-4.1zm56.6-69.5l-10 7.2-5-7.6 18-13h7v61.6h-10z" fill="#1a73e8" />
    </svg>
  );
}

function AppleCalendarIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5 shrink-0 text-neutral-700" fill="currentColor" aria-hidden>
      <path d="M19 4h-2V2h-2v2H9V2H7v2H5C3.9 4 3 4.9 3 6v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10zm0-12H5V6h14v2z" />
    </svg>
  );
}

function OutlookIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5 shrink-0" aria-hidden>
      <rect width="24" height="24" rx="4" fill="#0078d4" />
      <text
        x="12"
        y="16"
        textAnchor="middle"
        fontSize="11"
        fontWeight="700"
        fill="#fff"
        fontFamily="Arial, sans-serif"
      >
        O
      </text>
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="w-5 h-5 shrink-0 text-neutral-500"
      aria-hidden
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

